import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { NavLink, useLocation } from "react-router-dom";
import {
  Building2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  PanelRightClose,
  PanelRightOpen,
} from "lucide-react";
import BrandLogo from "./BrandLogo.js";


/**
 * Verifica se a rota atual pertence ao grupo. Reimplementado localmente para
 * NÃO depender do util da lib (`@lukos-tecnologia/ui`), que é symlinkada do
 * ErpOnline e traz uma instância DIFERENTE de react-router — o que fazia o
 * NavLink da sidebar colapsada quebrar com "useContext null". Função pura.
 */
function groupHasActiveRoute(pathname, group) {
  const p = (pathname || "").toLowerCase();
  if (group.href) {
    const href = group.href.toLowerCase();
    if (p === href || p.startsWith(href + "/")) return true;
  }
  return (group.items || []).some((it) => {
    if (!it.to) return false;
    const to = it.to.toLowerCase();
    return p === to || p.startsWith(to + "/");
  });
}

/**
 * Sidebar do shell (no Fatura usada como drawer no mobile). Modo expandido e
 * colapsado usam componentes/NavLink PRÓPRIOS do Fatura — não os da lib
 * `@lukos-tecnologia/ui`, que é symlinkada do ErpOnline e injeta outra
 * instância de react-router (causava "useContext null" no NavLink colapsado).
 */
export default function AppSidebar({
  sections,
  loading,
  collapsed,
  onToggleCollapsed,
  unitName = "—",
  unitPanelOpen = false,
  onUnitClick,
  onSearchClick,
}) {
  const location = useLocation();
  const [menuOpenState, setMenuOpenState] = useState({});
  const [flyoutKey, setFlyoutKey] = useState(null);

  const activeGroupKey = useMemo(() => {
    return (
      sections.find((g) => groupHasActiveRoute(location.pathname, g))?.key ?? null
    );
  }, [sections, location.pathname]);

  useEffect(() => {
    if (activeGroupKey) {
      setMenuOpenState((prev) =>
        prev[activeGroupKey] ? prev : { ...prev, [activeGroupKey]: true }
      );
    }
  }, [activeGroupKey]);

  useEffect(() => {
    if (!collapsed) setFlyoutKey(null);
  }, [collapsed]);

  useEffect(() => {
    setFlyoutKey(null);
  }, [location.pathname]);

  function toggleGroup(key) {
    setMenuOpenState((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function toggleFlyout(key) {
    setFlyoutKey((current) => (current === key ? null : key));
  }

  return (
    <aside className="app-sidebar" aria-label="Menu principal">
      <div className="app-sidebar__brand">
        <div className="app-sidebar__brand-symbol" aria-hidden>
          <BrandLogo size={38} />
        </div>
        <div className="app-sidebar__brand-text">
          <strong>Fatura WEB</strong>
          <span>Lukos Tecnologia</span>
        </div>
        <button
          className="app-sidebar__collapse"
          type="button"
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expandir menu" : "Minimizar menu"}
          title={collapsed ? "Expandir menu" : "Minimizar menu"}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {onUnitClick && (
        <div className="app-sidebar__footer">
          {!collapsed && (
            <span className="app-sidebar__unit-label">Unidade operacional</span>
          )}
          <button
            type="button"
            className="app-sidebar__footer-unit"
            onClick={onUnitClick}
            aria-expanded={unitPanelOpen}
            aria-controls="app-unit-panel"
            title={`${unitName} — Trocar unidade`}
          >
            <span className="app-sidebar__footer-unit-icon" aria-hidden>
              <Building2 size={15} strokeWidth={2.25} />
            </span>
            {!collapsed && (
              <>
                <span className="app-sidebar__footer-copy">
                  <span className="app-sidebar__footer-kicker">Unidade ativa</span>
                  <span className="app-sidebar__footer-unit-name">{unitName}</span>
                  <span className="app-sidebar__footer-action">Trocar unidade</span>
                </span>
                {unitPanelOpen ? (
                  <PanelRightClose size={14} strokeWidth={2} className="app-sidebar__footer-chev" />
                ) : (
                  <PanelRightOpen size={14} strokeWidth={2} className="app-sidebar__footer-chev" />
                )}
              </>
            )}
          </button>
        </div>
      )}

      <nav className="app-sidebar__nav">
        {loading && (
          <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
            <span className="app-sidebar__spinner" aria-label="Carregando" />
          </div>
        )}

        {!loading && sections.length > 0 && !collapsed && (
          <div className="app-sidebar__section-label">Menu</div>
        )}

        {!collapsed &&
          sections.map((group) => {
            if (group.items.length === 0) {
              return <DirectGroupLink key={group.key} group={group} />;
            }
            const isOpen = menuOpenState[group.key] ?? group.key === activeGroupKey;
            return (
              <ExpandedGroup
                key={group.key}
                group={group}
                isOpen={isOpen}
                isActiveGroup={group.key === activeGroupKey}
                onToggle={() => toggleGroup(group.key)}
              />
            );
          })}

        {collapsed &&
          sections.map((group) => {
            const routeActive = groupHasActiveRoute(location.pathname, group);
            if (group.items.length === 0) {
              return (
                <CollapsedDirectLink
                  key={group.key}
                  group={group}
                  routeActive={routeActive}
                />
              );
            }
            const flyoutOpen = flyoutKey === group.key;
            return (
              <CollapsedRow
                key={group.key}
                group={group}
                routeActive={routeActive}
                flyoutOpen={flyoutOpen}
                onToggleFlyout={() => toggleFlyout(group.key)}
                onCloseFlyout={() => setFlyoutKey(null)}
              />
            );
          })}
      </nav>
    </aside>
  );
}

function ExpandedGroup({ group, isOpen, isActiveGroup, onToggle }) {
  const GroupIcon = group.GroupIcon;
  return (
    <section className="app-shell__nav-group">
      <button
        type="button"
        className={`app-shell__submenu-trigger${isOpen ? " app-shell__submenu-trigger--open" : ""
          }`}
        onClick={onToggle}
        aria-expanded={isOpen}
      >
        <span className="app-shell__submenu-title">
          <GroupIcon size={16} strokeWidth={2} className="app-shell__nav-icon" />
          <span>{group.label}</span>
        </span>
        <ChevronDown
          size={14}
          className={`app-shell__chevron${isOpen ? " app-shell__chevron--open" : ""}`}
        />
      </button>

      {isOpen && (
        <div
          className={`app-shell__submenu-items${isActiveGroup ? " app-shell__submenu-items--active" : ""
            }`}
        >
          {group.items.map((item) => {
            const ItemIcon = item.Icon;
            return (
              <NavLink
                key={item.key}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `app-shell__nav-item${isActive ? " app-shell__nav-item--active" : ""}`
                }
              >
                <ItemIcon size={15} strokeWidth={2} className="app-shell__nav-icon" />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </section>
  );
}

function DirectGroupLink({ group }) {
  const GroupIcon = group.GroupIcon;
  return (
    <section className="app-shell__nav-group">
      <NavLink
        to={group.href || "#"}
        className={({ isActive }) =>
          `app-shell__submenu-trigger app-shell__submenu-trigger--link${isActive ? " app-shell__submenu-trigger--active" : ""
          }`
        }
      >
        <span className="app-shell__submenu-title">
          <GroupIcon size={16} strokeWidth={2} className="app-shell__nav-icon" />
          <span>{group.label}</span>
        </span>
      </NavLink>
    </section>
  );
}

function CollapsedDirectLink({ group, routeActive }) {
  const GroupIcon = group.GroupIcon;
  return (
    <div className="sidebar-nav-item-wrap">
      <NavLink
        to={group.href || "#"}
        className={({ isActive }) =>
          `sidebar-rail-btn${isActive || routeActive ? " sidebar-rail-btn--active" : ""}`
        }
        title={group.label}
      >
        <GroupIcon size={18} strokeWidth={1.75} />
      </NavLink>
    </div>
  );
}

function CollapsedRow({ group, routeActive, flyoutOpen, onToggleFlyout, onCloseFlyout }) {
  const triggerRef = useRef(null);
  const flyoutRef = useRef(null);
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!flyoutOpen) {
      setPos(null);
      return undefined;
    }
    function place() {
      const el = triggerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const margin = 8;
      const flyH = flyoutRef.current?.offsetHeight ?? 0;
      let top = rect.top;
      if (flyH && top + flyH > window.innerHeight - margin) {
        top = Math.max(margin, window.innerHeight - flyH - margin);
      }
      setPos({ top, left: rect.right + 10 });
    }
    place();
    const raf = requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [flyoutOpen]);

  useEffect(() => {
    if (!flyoutOpen) return undefined;
    function onDocMouseDown(e) {
      const t = e.target;
      if (triggerRef.current?.contains(t)) return;
      if (flyoutRef.current?.contains(t)) return;
      onCloseFlyout();
    }
    function onKey(e) {
      if (e.key === "Escape") onCloseFlyout();
    }
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [flyoutOpen, onCloseFlyout]);

  const GroupIcon = group.GroupIcon;

  return (
    <div ref={triggerRef} className="app-sidebar__rail-row">
      <button
        type="button"
        className={`sidebar-rail-btn${routeActive ? " sidebar-rail-btn--active" : ""}${flyoutOpen ? " sidebar-rail-btn--flyout" : ""
          }`}
        onClick={onToggleFlyout}
        aria-haspopup="true"
        aria-expanded={flyoutOpen}
        title={group.label}
      >
        <GroupIcon size={18} strokeWidth={1.75} />
      </button>

      {flyoutOpen &&
        createPortal(
          <div
            ref={flyoutRef}
            className="app-sidebar__rail-flyout-portal"
            style={{
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              visibility: pos ? "visible" : "hidden",
            }}
          >
            <div className="app-shell__nav-flyout">
              <div className="app-shell__nav-flyout-title">{group.label}</div>
              <div className="app-shell__nav-flyout-list">
                {group.items.map((item) => {
                  const ItemIcon = item.Icon;
                  return (
                    <NavLink
                      key={item.key}
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) =>
                        `app-shell__nav-flyout-link${isActive ? " app-shell__nav-flyout-link--active" : ""
                        }`
                      }
                      onClick={onCloseFlyout}
                    >
                      <span className="app-shell__nav-flyout-dot" aria-hidden />
                      {ItemIcon ? (
                        <ItemIcon size={15} strokeWidth={2} className="app-shell__nav-icon" />
                      ) : null}
                      <span>{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
